import { EditCaseActionDialog } from "components/CaseManagement/CaseForms/EditCaseActionDialog";
import {
  CaseActionStatusChip,
  ExternalStatusChip
} from "components/CaseManagement/CaseStream/CaseActionStatusChip";
import { StreamCard } from "components/CaseManagement/CaseStream/StreamCard";
import { StreamCardMenuOption } from "components/CaseManagement/CaseStream/StreamCardMenu";
import { CaseActionCommentSection } from "components/CaseManagement/Comments/CaseActionCommentSection";
import { ConfirmationDialog } from "components/ConfirmationDialog";
import { DateWithTooltip } from "components/DateWithTooltip";
import { CaseActionsContext } from "contexts/CaseActionsContext";
import { SnackbarQueueContext } from "contexts/SnackbarQueueContext";
import { UNASSIGNED } from "core/constants";
import buildUserName from "core/utils/build_user_name";
import { useFetchDeleteV2 } from "hooks/fetch/useFetchDeleteV2";
import { useFetchPutV2 } from "hooks/fetch/useFetchPutV2";
import { CaseAction } from "interfaces/case-action";
import { User } from "interfaces/user";
import { useContext, useMemo, useState } from "react";
import { styled } from "@mui/material/styles";

import Divider from "@mui/material/Divider";
import Skeleton from "@mui/material/Skeleton";
import Box from "@mui/system/Box";
import { FeaturesContext } from "contexts/FeaturesContext";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { API_URL_CASE_ACTIONS } from "constants/api_urls";
import { AxiosContext } from "contexts/AxiosContext";
import { ExistingAttachmentsList } from "components/Attachments/ExistingAttachmentsList";
import Button from "@mui/material/Button";
import Tooltip from "@mui/material/Tooltip";

export interface CaseActionCardProps {
  caseActionId: number;
  caseId: number;
  user: User;
  datetime: string;
}

export const CaseActionCard = ({
  caseActionId,
  user,
  datetime,
  caseId
}: CaseActionCardProps): JSX.Element => {
  const { axios } = useContext(AxiosContext)!;
  const { hasFeature } = useContext(FeaturesContext);
  const { loading, error, actions, refetchActions } = useContext(
    CaseActionsContext
  );
  const { addSnackbarToQueue } = useContext(SnackbarQueueContext);

  const [actionDialogOpen, setActionDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const queryClient = useQueryClient();

  const action = useMemo(
    () => actions.find(x => x.id === caseActionId) ?? null,
    [actions, caseActionId]
  );

  const attachmentsQuery = useQuery({
    queryKey: ["attachments", caseActionId],
    queryFn: async () => {
      const { data } = await axios.get(
        `${API_URL_CASE_ACTIONS}/${caseActionId}/attachments`
      );

      return data;
    },
    staleTime: Infinity,
    enabled: !!action
  });

  const submittedAttachmentsQuery = useQuery({
    queryKey: ["submitted_attachments", caseActionId],
    queryFn: async () => {
      const { data } = await axios.get(
        `${API_URL_CASE_ACTIONS}/${caseActionId}/submitted_attachments`
      );

      return data;
    },
    staleTime: Infinity,
    enabled: !!action
  });

  const { deleteCallback: deleteCaseAction } = useFetchDeleteV2(
    `case_actions/${action?.id}`
  );

  const { putCallback: putCaseAction } = useFetchPutV2(
    `case_actions/${action?.id}`
  );

  const handleError = (message: string): void => {
    addSnackbarToQueue({
      message: message,
      severity: "error"
    });
  };

  const handleDeleteCompleted = async (): Promise<void> => {
    addSnackbarToQueue({
      message: "Case action deleted.",
      severity: "success"
    });
    await queryClient.invalidateQueries(["streamFeed", caseId]);
    await refetchActions();
  };

  const handlePutCompleted = async (): Promise<void> => {
    addSnackbarToQueue({
      message: "Case action completed.",
      severity: "success"
    });
    await refetchActions();
  };

  const editItem = (): void => {
    setActionDialogOpen(true);
  };

  const completeItem = (): void => {
    if (action !== null) {
      let tempAction: CaseAction = { ...action };
      tempAction.action_status = 2;
      // TODO: fix the typing here
      (putCaseAction as (body: any) => Promise<any>)(tempAction).then(
        _fulfil => handlePutCompleted(),
        _reject => handleError("Case action failed to complete.")
      );
    }
  };

  const deleteItem = (): void => {
    deleteCaseAction().then(
      _fulfil => handleDeleteCompleted(),
      _reject => handleError("Case action failed to delete.")
    );
  };

  const getMenuOptions = (): StreamCardMenuOption[] => {
    const isActionComplete =
      action?.action_status === 2 || action?.action_status === 3;

    let menuOptions: StreamCardMenuOption[] = [
      { title: "Edit", onClick: () => editItem() }
    ];
    if (!isActionComplete) {
      menuOptions.push({
        title: "Complete",
        onClick: () => completeItem(),
        disabled: !!action?.is_external,
        tooltip: action?.is_external ? "Managed by external system" : undefined
      });
    }
    menuOptions.push({
      title: "Delete",
      onClick: () => setDeleteDialogOpen(true),
      disabled: !!action?.is_external,
      tooltip: action?.is_external ? "Managed by external system" : undefined
    });

    return menuOptions;
  };

  if (loading)
    return <Skeleton variant="rectangular" width="100%" height={200} />;

  // This is temporary until we have a UI design for when error is returned from useFetchDelete/useFetchGet
  if (error)
    return (
      <StreamCard user={user} datetime={datetime} label="Action">
        <br />
        <span>An Error has occurred </span>
      </StreamCard>
    );

  if (!action) {
    return <></>;
  }

  return (
    <>
      <StreamCard
        user={user}
        datetime={datetime}
        label="Action"
        menuOptions={getMenuOptions()}
        headerAdornment={
          <HeaderAdornmentRow>
            {action.is_external && (
              <ExternalStatusChip statusName={action.external_status} />
            )}
            <Tooltip title="Action status">
              <span>
                <CaseActionStatusChip
                  actionStatusName={action.action_status_detail.name}
                />
              </span>
            </Tooltip>
          </HeaderAdornmentRow>
        }
      >
        <ContentWrapper>
          <MultilineParagraph>
            <strong>Action ID:&nbsp;</strong>
            {action.id}
            <br />
            {action.is_external && (
              <>
                <strong>External ID:&nbsp;</strong>
                {action.external_id ?? <span>Not set</span>}
                <br />
              </>
            )}
            <strong>Action:&nbsp;</strong>
            {action.action_type_detail.name}
            <br />
            <strong>Assignee:&nbsp;</strong>
            {action.assignee_detail
              ? buildUserName(action.assignee_detail)
              : UNASSIGNED}
            <br />
            <strong>Due Date:&nbsp;</strong>
            {action.requested_completion_date ? (
              <DateWithTooltip date={action.requested_completion_date} />
            ) : (
              <span>Not set</span>
            )}
            <br />
            {action.is_external ? (
              <>
                <strong>Last updated externally:&nbsp;</strong>
                {action.last_updated_externally ? (
                  <DateWithTooltip
                    date={action.last_updated_externally}
                    showTime
                  />
                ) : (
                  <span>Not set</span>
                )}
              </>
            ) : (
              !hasFeature("my_actions") && (
                <>
                  <strong>Est. Completion Date:&nbsp;</strong>
                  {action.estimated_completion_date ? (
                    <DateWithTooltip date={action.estimated_completion_date} />
                  ) : (
                    <span>Not set</span>
                  )}
                </>
              )
            )}
            <MultilineParagraph>
              <strong>Description:</strong>
              <br />
              {action.description}
            </MultilineParagraph>
            {action.is_external && action.external_url && (
              <Button
                variant="contained"
                color="success"
                href={action.external_url}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  textTransform: "none",
                  mt: "10px",
                  // The button renders as <a> (because of href), so the
                  // user-agent :visited style turns the label purple.
                  color: theme =>
                    `${theme.palette.success.contrastText} !important`,
                  "&:visited": {
                    color: theme =>
                      `${theme.palette.success.contrastText} !important`
                  }
                }}
              >
                Open external request
              </Button>
            )}
          </MultilineParagraph>
          {attachmentsQuery.data && (
            <Box>
              <strong>Attachments:</strong>
              <ExistingAttachmentsList
                initialAttachments={attachmentsQuery.data}
                showUser={false}
              />
            </Box>
          )}
          {hasFeature("my_actions") && (
            <>
              <Divider />
              <MultilineParagraph>
                <strong>Summary of Work Scope: </strong>
                <br />
                {action.scope_of_work}
                <br />
                <strong>Estimated Completion Date: </strong>
                {action.estimated_completion_date ? (
                  <DateWithTooltip date={action.estimated_completion_date} />
                ) : (
                  <span>Not set</span>
                )}
                {submittedAttachmentsQuery.data && (
                  <Box>
                    <strong>Submitted Attachments: </strong>
                    <ExistingAttachmentsList
                      initialAttachments={submittedAttachmentsQuery.data}
                    />
                  </Box>
                )}
              </MultilineParagraph>
            </>
          )}
        </ContentWrapper>
        <Divider />
        <CaseActionCommentSection
          actionAssigneeId={action.assignee}
          actionId={action.id}
        />
      </StreamCard>
      {deleteDialogOpen && (
        <ConfirmationDialog
          open={deleteDialogOpen}
          title="Are you sure you want to delete this action?"
          onConfirm={() => {
            deleteItem();
            setDeleteDialogOpen(false);
          }}
          onCancel={() => setDeleteDialogOpen(false)}
          onClose={() => setDeleteDialogOpen(false)}
        />
      )}
      {actionDialogOpen && (
        <EditCaseActionDialog
          open={actionDialogOpen}
          setOpen={setActionDialogOpen}
          action={action}
          caseId={caseId}
          onReplaceExistingAction={() => refetchActions()}
        />
      )}
    </>
  );
};

const ContentWrapper = styled("div")`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const MultilineParagraph = styled("p")`
  white-space: pre-wrap;
  margin: 0;
`;

const HeaderAdornmentRow = styled("div")`
  display: flex;
  align-items: center;
  gap: 6px;
`;
